/**
 * Copyright © 2026 Delicious AI, LLC
 */

import {
  UltraCompanyChoices,
  UltraLabelingExplorerBoxStatus,
  UltraLabelingExplorerMode,
  UltraLabelingExplorerSort,
  type UltraLabelingExplorerInput,
} from '@dai/graphql'
import { format, subDays } from 'date-fns'

import type { LabelingExplorerItem } from './explorerUtils'

export const defaultCreatedAfter = () =>
  format(subDays(new Date(), 7), 'yyyy-MM-dd')

export const PAGE_SIZE = 30
export const DEFAULT_CLOSE_CONFIDENCE_THRESHOLD = 0.05
export const PRODUCT_LOOKUP_BATCH_SIZE = 25
export const DEFAULT_CROP_TILE_SIZE = 180
export const MIN_CROP_TILE_SIZE = 120
export const MAX_CROP_TILE_SIZE = 320
export const CROP_TILE_SIZE_STEP = 20

export const SOURCE_CUSTOMERS: UltraCompanyChoices[] = [
  UltraCompanyChoices.SWIRE,
  UltraCompanyChoices.REYES,
]

export const SORT_OPTIONS: {
  value: UltraLabelingExplorerSort
  label: string
}[] = [
  { value: UltraLabelingExplorerSort.DATE_CREATED, label: 'Date created' },
  { value: UltraLabelingExplorerSort.DATE_VERIFIED, label: 'Date verified' },
]

export const BOX_STATUSES: UltraLabelingExplorerBoxStatus[] = [
  UltraLabelingExplorerBoxStatus.UNIDENTIFIED,
  UltraLabelingExplorerBoxStatus.CLOSE_TOP_2,
  UltraLabelingExplorerBoxStatus.VERIFIED,
  UltraLabelingExplorerBoxStatus.UNVERIFIED,
]

export type VisualSearchState = {
  searchId: number
  anchorItem: LabelingExplorerItem | null
  anchorImagePreview: string | null
  loading: boolean
  items: LabelingExplorerItem[]
  error: string | null
}

export const queryKeys = {
  mode: 'mode',
  sort: 'sort',
  sourceCustomer: 'sourceCustomer',
  upcs: 'upcs',
  statuses: 'statuses',
  minUnidentifiedCount: 'minUnidentifiedCount',
  maxUnidentifiedCount: 'maxUnidentifiedCount',
  minImageAcquisitionScore: 'minImageAcquisitionScore',
  maxImageAcquisitionScore: 'maxImageAcquisitionScore',
  minBoxAcquisitionScore: 'minBoxAcquisitionScore',
  maxBoxAcquisitionScore: 'maxBoxAcquisitionScore',
  createdAfter: 'createdAfter',
  createdBefore: 'createdBefore',
  cropSize: 'cropSize',
} as const

export type FilterState = {
  sourceCustomer: '' | UltraCompanyChoices
  upcs: string
  statuses: UltraLabelingExplorerBoxStatus[]
  minUnidentifiedCount: string
  maxUnidentifiedCount: string
  minImageAcquisitionScore: string
  maxImageAcquisitionScore: string
  minBoxAcquisitionScore: string
  maxBoxAcquisitionScore: string
  createdAfter: string
  createdBefore: string
}

export const initialFilters: FilterState = {
  sourceCustomer: '',
  upcs: '',
  statuses: [],
  minUnidentifiedCount: '',
  maxUnidentifiedCount: '',
  minImageAcquisitionScore: '',
  maxImageAcquisitionScore: '',
  minBoxAcquisitionScore: '',
  maxBoxAcquisitionScore: '',
  createdAfter: '',
  createdBefore: '',
}

export const isUltraCompanyChoices = (
  value: string | null,
): value is UltraCompanyChoices =>
  SOURCE_CUSTOMERS.includes(value as UltraCompanyChoices)

export const readParam = (
  searchParams: URLSearchParams,
  key: string,
  fallback = '',
) => (searchParams.has(key) ? searchParams.get(key) ?? '' : fallback)

export const readMode = (
  searchParams: URLSearchParams,
): UltraLabelingExplorerMode =>
  searchParams.get(queryKeys.mode) === 'CROP'
    ? UltraLabelingExplorerMode.CROP
    : UltraLabelingExplorerMode.IMAGE

export const readSort = (
  searchParams: URLSearchParams,
): UltraLabelingExplorerSort =>
  searchParams.get(queryKeys.sort) === 'DATE_VERIFIED'
    ? UltraLabelingExplorerSort.DATE_VERIFIED
    : UltraLabelingExplorerSort.DATE_CREATED

export const readCropTileSize = (searchParams: URLSearchParams) => {
  const raw = searchParams.get(queryKeys.cropSize)
  if (raw === null) return DEFAULT_CROP_TILE_SIZE
  const parsed = Number(raw)
  if (Number.isNaN(parsed)) return DEFAULT_CROP_TILE_SIZE
  return Math.min(Math.max(parsed, MIN_CROP_TILE_SIZE), MAX_CROP_TILE_SIZE)
}

export const normalizeCropTileSize = (value: number) => {
  if (!Number.isFinite(value)) return DEFAULT_CROP_TILE_SIZE
  const steppedValue =
    Math.round((value - MIN_CROP_TILE_SIZE) / CROP_TILE_SIZE_STEP) *
      CROP_TILE_SIZE_STEP +
    MIN_CROP_TILE_SIZE
  return Math.min(
    Math.max(steppedValue, MIN_CROP_TILE_SIZE),
    MAX_CROP_TILE_SIZE,
  )
}

export const readFilters = (searchParams: URLSearchParams): FilterState => {
  const sourceCustomer = searchParams.get(queryKeys.sourceCustomer)
  const statusesRaw = searchParams.get(queryKeys.statuses)
  const statuses = statusesRaw
    ? statusesRaw
        .split(',')
        .filter((s): s is UltraLabelingExplorerBoxStatus =>
          BOX_STATUSES.includes(s as UltraLabelingExplorerBoxStatus),
        )
    : []
  return {
    sourceCustomer: isUltraCompanyChoices(sourceCustomer) ? sourceCustomer : '',
    upcs: readParam(searchParams, queryKeys.upcs),
    statuses,
    minUnidentifiedCount: readParam(
      searchParams,
      queryKeys.minUnidentifiedCount,
    ),
    maxUnidentifiedCount: readParam(
      searchParams,
      queryKeys.maxUnidentifiedCount,
    ),
    minImageAcquisitionScore: readParam(
      searchParams,
      queryKeys.minImageAcquisitionScore,
    ),
    maxImageAcquisitionScore: readParam(
      searchParams,
      queryKeys.maxImageAcquisitionScore,
    ),
    minBoxAcquisitionScore: readParam(
      searchParams,
      queryKeys.minBoxAcquisitionScore,
    ),
    maxBoxAcquisitionScore: readParam(
      searchParams,
      queryKeys.maxBoxAcquisitionScore,
    ),
    // Default to last 7 days when not explicitly set; empty string means "all time"
    createdAfter: searchParams.has(queryKeys.createdAfter)
      ? searchParams.get(queryKeys.createdAfter) ?? ''
      : defaultCreatedAfter(),
    createdBefore: readParam(searchParams, queryKeys.createdBefore),
  }
}

export const updateSearchParam = (
  searchParams: URLSearchParams,
  key: string,
  value: string | null,
) => {
  const next = new URLSearchParams(searchParams)
  if (value === null) next.delete(key)
  else next.set(key, value)
  return next
}

export const updateSearchParams = (
  searchParams: URLSearchParams,
  entries: [string, string | null][],
) => {
  const next = new URLSearchParams(searchParams)
  entries.forEach(([key, value]) => {
    if (value === null) next.delete(key)
    else next.set(key, value)
  })
  return next
}

export const parseOptionalFloat = (value: string) => {
  const parsed = Number(value)
  return value.trim() === '' || Number.isNaN(parsed) ? undefined : parsed
}

export const parseOptionalInt = (value: string) => {
  const parsed = Number.parseInt(value, 10)
  return value.trim() === '' || Number.isNaN(parsed) ? undefined : parsed
}

export const optionalFilter = <T extends string, V>(
  key: T,
  value: V | undefined,
) => (value === undefined ? {} : { [key]: value })

export const parseUpcs = (value: string) =>
  value
    .split(/[\s,]+/)
    .map(upc => upc.trim())
    .filter(Boolean)

export const formatUpcFilterValue = (upcs: string[]) => {
  const normalizedUpcs = Array.from(
    new Set(upcs.flatMap(upc => parseUpcs(upc))),
  )
  return normalizedUpcs.length ? normalizedUpcs.join(',') : null
}

export const formatRangeValue = (value: number, step: number) => {
  if (step >= 1) return String(Math.round(value))
  return value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
}

export const readRangeEndpoint = (
  value: string,
  fallback: number,
  min: number,
  max: number,
) => {
  const parsed = Number(value)
  if (value.trim() === '' || Number.isNaN(parsed)) return fallback
  return Math.min(Math.max(parsed, min), max)
}

const dateToUtcIso = (dateStr: string): string =>
  new Date(dateStr + 'T00:00:00.000Z').toISOString()

const dateToDayAfterUtcIso = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00.000Z')
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString()
}

export const buildExplorerInput = (
  mode: UltraLabelingExplorerMode,
  sort: UltraLabelingExplorerSort,
  filters: FilterState,
  after: string | null = null,
): UltraLabelingExplorerInput => {
  const upcs = parseUpcs(filters.upcs)
  return {
    mode,
    sort,
    first: PAGE_SIZE,
    after,
    filters: {
      ...(filters.sourceCustomer
        ? { sourceCustomer: filters.sourceCustomer }
        : {}),
      ...(upcs.length ? { upcs } : {}),
      ...(filters.statuses.length ? { statuses: filters.statuses } : {}),
      ...(filters.createdAfter
        ? { createdAfter: dateToUtcIso(filters.createdAfter) }
        : {}),
      ...(filters.createdBefore
        ? { createdBefore: dateToDayAfterUtcIso(filters.createdBefore) }
        : {}),
      ...optionalFilter(
        'minUnidentifiedCount',
        parseOptionalInt(filters.minUnidentifiedCount),
      ),
      ...optionalFilter(
        'maxUnidentifiedCount',
        parseOptionalInt(filters.maxUnidentifiedCount),
      ),
      ...optionalFilter(
        'minImageAcquisitionScore',
        parseOptionalFloat(filters.minImageAcquisitionScore),
      ),
      ...optionalFilter(
        'maxImageAcquisitionScore',
        parseOptionalFloat(filters.maxImageAcquisitionScore),
      ),
      ...optionalFilter(
        'minBoxAcquisitionScore',
        parseOptionalFloat(filters.minBoxAcquisitionScore),
      ),
      ...optionalFilter(
        'maxBoxAcquisitionScore',
        parseOptionalFloat(filters.maxBoxAcquisitionScore),
      ),
    },
  }
}
